
import { Component } from '@theme/component';
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
} from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer
 * @property {HTMLElement} headerMenu
 * @property {HTMLElement} headerRowTop
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>}
 * OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = [
    'headerDrawerContainer',
    'headerMenu',
    'headerRowTop',
  ];

  /**
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * @type {number | null}
   */
  #timeout = null;

  /**
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global header-height variable up to date.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const roundedHeaderHeight = Math.round(
      entry.borderBoxSize[0].blockSize
    );

    document.body.style.setProperty(
      '--header-height',
      `${roundedHeaderHeight}px`
    );

    if (
      this.#menuDrawerHiddenWidth &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the sticky header position.
   *
   * @param {boolean} alwaysSticky
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        const { isIntersecting } = entry;

        if (alwaysSticky) {
          this.dataset.stickyState = isIntersecting
            ? 'inactive'
            : 'active';

          if (this.dataset.themeColor) {
            changeMetaThemeColor(this.dataset.themeColor);
          }
        } else {
          this.#offscreen =
            !isIntersecting ||
            this.dataset.stickyState === 'active';
        }
      },
      config
    );

    this.#intersectionObserver.observe(this);
  };

  /**
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(
      event.detail.minimumReached
    );
  };

  /**
   * @param {boolean} hideMenu
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }

    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');

    if (!this.#offscreen && stickyMode !== 'always') {
      return;
    }

    const scrollTop =
      document.scrollingElement?.scrollTop ?? 0;

    const headerTop =
      this.getBoundingClientRect().top;

    const isScrollingUp =
      scrollTop < this.#lastScrollTop;

    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);

    this.addEventListener(
      'overflowMinimum',
      this.#handleOverflowMinimum
    );

    const stickyMode = this.getAttribute('sticky');

    if (stickyMode) {
      this.#observeStickyPosition(
        stickyMode === 'always'
      );

      if (
        stickyMode === 'scroll-up' ||
        stickyMode === 'always'
      ) {
        document.addEventListener(
          'scroll',
          this.#handleWindowScroll
        );
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();

    this.#intersectionObserver?.disconnect();

    this.removeEventListener(
      'overflowMinimum',
      this.#handleOverflowMinimum
    );

    document.removeEventListener(
      'scroll',
      this.#handleWindowScroll
    );

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    document.body.style.setProperty(
      '--header-height',
      '0px'
    );
  }
}

if (!customElements.get('header-component')) {
  customElements.define(
    'header-component',
    HeaderComponent
  );
}

/**
 * Shared exhibition header detection.
 *
 * This recognises exhibition pages by their sections,
 * not by their Shopify template name.
 *
 * It therefore also works on duplicated exhibition
 * templates with new suffixes.
 */
function initialiseExhibitionHeader() {
  const header = document.querySelector(
    '#header-component'
  );

  if (!header) return;

  const hero = document.querySelector(
    'main .hero[data-hp-header-overlay="true"]'
  );

  const exhibitionOverview =
    document.querySelector(
      'main .exhibition-overview__layout'
    );

  // Only activate where the exhibition hero
  // explicitly requests a header overlay.
  if (!hero || !exhibitionOverview) return;

  // Do not change the homepage or non-exhibition pages.
  if (!document.body || !header.closest('header')) {
    return;
  }

  // The existing Joel header already has this setting.
  // Setting the same value is harmless and keeps all
  // exhibition templates consistent.
  header.setAttribute(
    'transparent',
    'not-sticky'
  );

  header.setAttribute(
    'data-exhibition-hero-overlay',
    ''
  );

  // Exhibition headers use the normal sticky behaviour,
  // not the homepage's always-transparent presentation.
  header.removeAttribute(
    'data-home-transparent-overlay'
  );

  // Add the styling only once.
  if (
    document.getElementById(
      'hp-shared-exhibition-header-styles'
    )
  ) {
    return;
  }

  const style = document.createElement('style');

  style.id =
    'hp-shared-exhibition-header-styles';

  style.textContent = `
    /*
     * Shared exhibition header.
     *
     * Transparent at the top, with white branding.
     * White background and black branding on scroll.
     */

    #header-component[data-exhibition-hero-overlay] {
      --header-logo-display: none;
      --header-logo-inverse-display: block;
    }

    #header-component[data-exhibition-hero-overlay]:not(
      [data-sticky-state="active"]
    ) {
      --color-foreground: #ffffff;
      --color-foreground-rgb: 255 255 255;
      --color-border: #ffffff;
      --color-border-rgb: 255 255 255;
      --closed-underlay-height: 0px;
    }

    #header-component[data-exhibition-hero-overlay]:not(
      [data-sticky-state="active"]
    ) .header__row--top {
      --color-foreground: #ffffff;
      --color-foreground-rgb: 255 255 255;
    }

    #header-component[data-exhibition-hero-overlay]:not(
      [data-sticky-state="active"]
    ) :is(
      .header__icon--menu,
      .search-action,
      .header-actions__action
    ) {
      color: #ffffff;
    }

    /*
     * Fallback for newly duplicated templates
     * that have not rendered an inverse logo.
     *
     * The original black logo is displayed in
     * white without changing the source image.
     */
    #header-component[data-exhibition-hero-overlay]:not(
      [data-sticky-state="active"]
    ) .header-logo:not(
      :has(.header-logo__image-container--inverse)
    ) .header-logo__image-container--original {
      display: block;
      filter: brightness(0) invert(1);
    }

    /*
     * Sticky, scrolled exhibition header.
     */
    #header-component[data-exhibition-hero-overlay][
      data-sticky-state="active"
    ] {
      --header-logo-display: block;
      --header-logo-inverse-display: none;
      --color-foreground: #000000;
      --color-foreground-rgb: 0 0 0;
      --color-border: #000000;
      --color-border-rgb: 0 0 0;
      --closed-underlay-height: 100%;
    }

    #header-component[data-exhibition-hero-overlay][
      data-sticky-state="active"
    ] .header__underlay-closed {
      height: 100%;
      background: #ffffff;
    }

    #header-component[data-exhibition-hero-overlay][
      data-sticky-state="active"
    ] .header__row--top {
      --color-foreground: #000000;
      --color-foreground-rgb: 0 0 0;
      color: #000000;
    }

    #header-component[data-exhibition-hero-overlay][
      data-sticky-state="active"
    ] :is(
      .header__icon--menu,
      .search-action,
      .header-actions__action
    ) {
      color: #000000;
    }

    #header-component[data-exhibition-hero-overlay][
      data-sticky-state="active"
    ] .header-logo__image-container--original {
      display: block;
      filter: none;
    }

    #header-component[data-exhibition-hero-overlay][
      data-sticky-state="active"
    ] .header-logo__image-container--inverse {
      display: none;
    }
  `;

  document.head.appendChild(style);
}

onDocumentLoaded(() => {
  /*
   * Initialise exhibition overlay behaviour
   * before performing normal header measurements.
   */
  initialiseExhibitionHeader();

  const header = document.querySelector(
    'header-component'
  );

  const headerGroup = document.querySelector(
    '#header-group'
  );

  // Initial header heights are set by theme.liquid.
  // ResizeObserver handles updates after page load.

  if (headerGroup) {
    const resizeObserver = new ResizeObserver(
      (entries) => {
        const headerGroupHeight = entries.reduce(
          (totalHeight, entry) => {
            if (
              entry.target !== header ||
              (
                header?.hasAttribute('transparent') &&
                header.parentElement?.nextElementSibling
              )
            ) {
              return (
                totalHeight +
                (entry.borderBoxSize[0]?.blockSize ?? 0)
              );
            }

            return totalHeight;
          },
          0
        );

        const roundedHeaderGroupHeight =
          Math.round(headerGroupHeight);

        document.body.style.setProperty(
          '--header-group-height',
          `${roundedHeaderGroupHeight}px`
        );
      }
    );

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe every child of the header group.
    const children = headerGroup.children;

    for (let i = 0; i < children.length; i++) {
      const element = children[i];

      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Handle elements added dynamically.
    const mutationObserver = new MutationObserver(
      (mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            const children = headerGroup.children;

            for (
              let i = 0;
              i < children.length;
              i++
            ) {
              const element = children[i];

              if (element instanceof HTMLElement) {
                resizeObserver.observe(element);
              }
            }
          }
        }
      }
    );

    mutationObserver.observe(headerGroup, {
      childList: true,
    });
  }

  /*
   * Recheck when Shopify reloads a section
   * inside the theme editor.
   */
  document.addEventListener(
    'shopify:section:load',
    initialiseExhibitionHeader
  );
});
